// App.js
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { AuthProvider, useAppAuth } from "./context/AuthContext";
import Sidebar from "./components/sidebar";
import ProtectedRoute from "./components/ProtectedRoute";
import Login from "./pages/Login";
import Callback from "./pages/Callback";
import Dashboard from "./pages/Dashboard";
import UsersPage from "./pages/UsersPage";
import Items from "./pages/Items";
import Services from "./pages/Services";
import Airbnbs from "./pages/Airbnbs";
import Properties from "./pages/properties";
import Unauthorized from "./pages/Unauthorized";
import ShopsEntry from "./components/ShopsEntry";
import ShopDashboard from "./pages/ShopDashboard";
import ShopInfo from "./pages/ShopInfo";
import ProductsManager from "./pages/ProductsManager";
import DealsManager from "./pages/DealsManager";
import api, { setAuthToken } from "./utils/axiosInstance";
import { useEffect, useState } from "react";
import { Box } from "@mui/material";

// ✅ Wrapper for seller’s own shop (create if none, manage if exists)
const MyShopWrapper = () => {
  const { appUser } = useAppAuth();
  const { getAccessTokenSilently } = useAuth0();
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMyShop = async () => {
      if (!appUser || !getAccessTokenSilently) return;

      try {
        setLoading(true);

        // ✅ Get token with audience so backend can validate
        const token = await getAccessTokenSilently({
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        });
        setAuthToken(token);

        const res = await api.get("/shops/my");
        setShop(res.data || null);
      } catch (err) {
        console.error("fetchMyShop error:", err);
        setShop(null);
      } finally {
        setLoading(false);
      }
    };

    if (appUser?.role?.toLowerCase() === "seller") {
      fetchMyShop();
    } else {
      setLoading(false);
    }
  }, [appUser, getAccessTokenSilently]);

  if (loading) return <p>Loading...</p>;

  if (!shop) {
    return (
      <ShopInfo
        onShopUpdate={(createdShop) => {
          setShop(createdShop);
        }}
      />
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* ShopInfo edit form */}
      <ShopInfo shop={shop} onShopUpdate={(updatedShop) => setShop(updatedShop)} />

      {/* Product management */}
      <Box sx={{ mt: 4 }}>
        <h3>Products</h3>
        <ProductsManager shopSlug={shop.slug} />
      </Box>

      {/* Premium only: Deals */}
      {shop.is_premium && (
        <Box sx={{ mt: 4 }}>
          <h3>Deals</h3>
          <DealsManager shopSlug={shop.slug} isPremium={shop.is_premium} />
        </Box>
      )}
    </Box>
  );
};

function App() {
  return (
    <Auth0Provider
      domain={process.env.REACT_APP_AUTH0_DOMAIN}
      clientId={process.env.REACT_APP_AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin + "/callback", // ✅ include /callback
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
      }}
    >
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public pages */}
            <Route path="/login" element={<Login />} />
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route path="/callback" element={<Callback />} />

            {/* Protected area */}
            <Route
              path="/*"
              element={
                <ProtectedRoute allowedRoles={["admin", "seller", "super_admin"]}>
                  <Box sx={{ display: "flex" }}>
                    <Sidebar />
                    <Box
                      component="main"
                      sx={{
                        flexGrow: 1,
                        p: { xs: 4, sm: 3 },
                        ml: { xs: 0, sm: 30 }, // 240px on desktop
                        width: { xs: "100%", sm: `calc(100% - 240px)` },
                      }}
                    >
                      <Routes>
                        {/* Dashboard */}
                        <Route
                          path="/"
                          element={
                            <ProtectedRoute allowedRoles={["admin", "seller"]}>
                              <Dashboard />
                            </ProtectedRoute>
                          }
                        />

                        {/* Users */}
                        <Route
                          path="/users"
                          element={
                            <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                              <UsersPage />
                            </ProtectedRoute>
                          }
                        />

                        {/* Shops entry */}
                        <Route
                          path="/shops"
                          element={
                            <ProtectedRoute allowedRoles={["seller", "admin", "super_admin"]}>
                              <ShopsEntry />
                            </ProtectedRoute>
                          }
                        />

                        {/* Seller’s own shop */}
                        <Route
                          path="/shops/my"
                          element={
                            <ProtectedRoute allowedRoles={["seller"]}>
                              <MyShopWrapper />
                            </ProtectedRoute>
                          }
                        />

                        {/* Shop dashboard by slug */}
                        <Route
                          path="/shops/:slug/dashboard"
                          element={
                            <ProtectedRoute allowedRoles={["seller", "admin", "super_admin"]}>
                              <ShopDashboard />
                            </ProtectedRoute>
                          }
                        />

                        {/* Admin-only sections */}
                        <Route
                          path="/items"
                          element={
                            <ProtectedRoute allowedRoles={["admin"]}>
                              <Items />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/services"
                          element={
                            <ProtectedRoute allowedRoles={["admin"]}>
                              <Services />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/airbnbs"
                          element={
                            <ProtectedRoute allowedRoles={["admin"]}>
                              <Airbnbs />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/properties"
                          element={
                            <ProtectedRoute allowedRoles={["admin"]}>
                              <Properties />
                            </ProtectedRoute>
                          }
                        />

                        {/* Deals for premium sellers */}
                        <Route
                          path="/deals"
                          element={
                            <ProtectedRoute allowedRoles={["seller"]}>
                              <DealsManager />
                            </ProtectedRoute>
                          }
                        />
                      </Routes>
                    </Box>
                  </Box>
                </ProtectedRoute>
              }
            />
          </Routes>
        </Router>
      </AuthProvider>
    </Auth0Provider>
  );
}

export default App;
